import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Calculator, Book, Zap } from "lucide-react";
import { motion } from "framer-motion";

export function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-border/40">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-20" />
      
      <div className="container relative py-24 md:py-32 lg:py-40">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="secondary" className="mb-6 gap-2 px-4 py-2">
              <Sparkles className="h-4 w-4" />
              Powered by AI
            </Badge>
          </motion.div>

          <motion.h1 
            className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            AI-Powered MEP Engineering
            <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              at Your Fingertips
            </span>
          </motion.h1>

          <motion.p 
            className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground md:text-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Your intelligent assistant for HVAC, Electrical, Firefighting, and Plumbing engineering.
            Calculations, codes, real projects, and AI-powered insights in one platform.
          </motion.p>

          <motion.div 
            className="flex flex-col gap-4 sm:flex-row sm:justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button size="lg" className="gap-2 text-base">
              Start Free Trial
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-base">
              Watch Demo
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="mt-16 grid grid-cols-3 gap-8 border-t border-border/40 pt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div>
              <div className="mb-1 text-3xl font-bold text-primary">50+</div>
              <div className="text-sm text-muted-foreground">Calculations</div>
            </div>
            <div>
              <div className="mb-1 text-3xl font-bold text-primary">15+</div>
              <div className="text-sm text-muted-foreground">Disciplines</div>
            </div>
            <div>
              <div className="mb-1 text-3xl font-bold text-primary">24/7</div>
              <div className="text-sm text-muted-foreground">AI Assistant</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Feature Icons */}
      <div className="absolute left-10 top-1/4 hidden lg:block">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="rounded-lg border border-border/40 bg-card/50 p-3 backdrop-blur"
        >
          <Calculator className="h-6 w-6 text-primary" />
        </motion.div>
      </div>
      <div className="absolute right-10 top-1/3 hidden lg:block">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
          className="rounded-lg border border-border/40 bg-card/50 p-3 backdrop-blur"
        >
          <Book className="h-6 w-6 text-accent" />
        </motion.div>
      </div>
      <div className="absolute bottom-20 left-1/4 hidden lg:block">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity, delay: 1 }}
          className="rounded-lg border border-border/40 bg-card/50 p-3 backdrop-blur"
        >
          <Zap className="h-6 w-6 text-primary" />
        </motion.div>
      </div>
    </section>
  );
}
